import { Add, Save, DeleteOutlined } from '@mui/icons-material';
import { Button, CircularProgress, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import { FC, useMemo } from 'react';

import { Table, ITable } from '../../components';

/**
 * Reusable presentational component for operations on a resource, `rows`, while
 * `columns` determines the visual content.
 */
// Potential improvements:
// - Formik
// - Yup Validation
export const AdminTable: FC<
  ITable & {
    isLoading: boolean;
    data: readonly Record<string, any>[];
    columns: any[];
    onAdd?(): void;
    onSave(): Promise<void>;
    onDelete?(value: any, index: number): void;
    isSaving: boolean;
    hasChanges: boolean;
    onAddFocusClass?: string;
    isSaveDisabled?: boolean;
  }
> = ({
  onAdd,
  onSave,
  onDelete,
  isSaving,
  hasChanges,
  onAddFocusClass,
  data,
  columns,
  isLoading,
  isSaveDisabled = false,
  ...tableProps
}) => {
  // Append the last column, the actions column
  const fullColumns = useMemo(() => {
    return columns.concat({
      id: 'actions',
      sort: false,
      overrideWidth: '8rem',
      accessor: '',
      disableSortBy: true,
      Header: () => {
        return (
          <Box
            display="flex"
            justifyContent="end"
            sx={{
              '& > *:not(:last-child)': {
                marginRight: theme => theme.spacing(1),
              },
              '.MuiButton-root.Mui-disabled': {
                border: '1px solid rgba(140, 155, 147, 0.5)',
                color: 'rgb(255 255 255 / 26%)',
                backgroundColor: '#19667E',
              },
            }}
          >
            {/** ADD, SAVE BUTTONS */}
            {!!onAdd && (
              <Button
                sx={theme => ({
                  color: '#fff',
                  borderColor: 'rgb(255 255 255 / 50%)',
                  '&:hover': {
                    color: theme.frankie.searchBlue,
                    borderColor: '#ffffff',
                    backgroundColor: '#ffffff',
                  },
                })}
                startIcon={<Add />}
                variant="outlined"
                onClick={() => {
                  onAdd?.();

                  if (onAddFocusClass) {
                    Promise.resolve().then(() => {
                      const arr = Array.from(document.getElementsByClassName(onAddFocusClass));
                      arr?.[arr.length - 1]?.scrollIntoView?.();
                    });
                  }
                }}
              >
                Add
              </Button>
            )}
            <Button
              startIcon={
                isSaving ? (
                  <CircularProgress variant="indeterminate" size={20} color="primary" />
                ) : (
                  <Save />
                )
              }
              disabled={!hasChanges || isSaving || isSaveDisabled}
              sx={theme => ({
                ...(hasChanges && {
                  color: theme.frankie.searchBlue,
                  background: '#fff',
                  transition: 'none',
                  borderColor: 'rgb(255 255 255 / 100%)',
                  '&:hover': {
                    color: theme.frankie.searchBlue,
                    borderColor: 'rgb(255 255 255 / 100%)',
                    background: '#fff',
                  },
                }),
              })}
              variant="outlined"
              onClick={onSave}
            >
              Save
            </Button>
          </Box>
        );
      },
      verticalAlignment: 'top',
      Cell: ({
        cell: {
          row: { original, index },
        },
      }: {
        cell: { row: { original: any; index: number } };
      }) => {
        if (!onDelete) {
          return null;
        }

        return (
          <Box display="flex" justifyContent="center">
            <IconButton color="error" tabIndex={-1} onClick={() => onDelete?.(original, index)}>
              <DeleteOutlined />
            </IconButton>
          </Box>
        );
      },
    });
  }, [columns, hasChanges, isSaveDisabled, isSaving, onAdd, onAddFocusClass, onDelete, onSave]);

  return (
    <Box
      sx={{
        maxWidth: '100%',
        overflowX: 'auto',
        overflow: 'unset',
        position: 'relative',
        minHeight: 100,
        '& table': {
          width: '100%',
          tableLayout: 'auto',
        },
        '& th:first-of-type, & td:first-of-type': {
          minWidth: '100px !important',
          width: '250px !important',
          maxWidth: '300px !important',
          paddingLeft: '5px !important',
          textAlign: 'left !important',
        },
      }}
      display="flex"
      width="100%"
      alignItems="center"
    >
      <Table
        {...tableProps}
        stickyHeader
        hidePagination
        data={data}
        columns={fullColumns}
        isLoading={isLoading}
      />
    </Box>
  );
};
